import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@volca/ui/lib/utils";
import { AccessRow } from "./access-row";
import { ChangeStepper } from "./change-stepper";
import { ARTIFACT_CONTROLS, PANE_BOTTOM_CLEARANCE } from "./constants";
import { PaneSearchControl } from "./pane-search-control";
import { useEditorAccessKeys } from "./query";
import { RulesAccessSkeleton } from "./rules-access-skeleton";
import { editorError } from "./state";
import type { AccessKeyView } from "./types";
import { usePaneSearch } from "./use-pane-search";

/**
 * Reads its own query rather than being handed the answer.
 *
 * The parent used to fetch this and pass the data down, which meant the parent
 * also owned the loading state and chose the skeleton for a pane it does not
 * draw. React Query serves both callers from one request, so the only thing
 * that changed is who knows when the data is ready: the component that renders
 * it.
 */
export const RulesAccess = ({
  businessId,
  workspaceId,
  active,
}: {
  businessId: string;
  workspaceId: string;
  active: boolean;
}) => {
  const query = useEditorAccessKeys(businessId, workspaceId, active);
  const rows = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0);
  const access: AccessKeyView | undefined = query.data;
  // The group's name is what a reader searches for. The counts beside it are
  // numbers they are reading, not words they would type.
  const search = usePaneSearch(
    useMemo(
      () =>
        (access?.keys ?? []).map((entry) => ({
          id: entry.key,
          text: entry.label,
        })),
      [access?.keys],
    ),
    rows,
  );
  // Numbered over the rendered order, so the nth step and the nth marked row
  // down the pane are the same group. Counted from the rows rather than from
  // the four server lists, which are keyed by name and say nothing about where
  // a group sits.
  const steps: Record<string, number> = {};
  (access?.keys ?? []).forEach((entry) => {
    if (entry.state !== "unchanged")
      steps[entry.key] = Object.keys(steps).length;
  });
  const changed = Object.keys(steps).length;
  // Derived, not stored: a reread can leave fewer changes than before and a
  // kept index would point past the end.
  const at = changed ? ((step % changed) + changed) % changed : 0;
  // The active match is brought into view the same way a stepped change is.
  useEffect(() => {
    if (!search.activeId) return;
    const node = rows.current?.querySelector(
      `[data-key="${CSS.escape(search.activeId)}"]`,
    );
    const viewport = rows.current?.closest<HTMLElement>(
      "[data-scroll-viewport]",
    );
    if (!node || !viewport) return;
    viewport.scrollTop +=
      node.getBoundingClientRect().top -
      viewport.getBoundingClientRect().top -
      viewport.clientHeight / 2;
  }, [search.activeId]);
  const goTo = (by: number) => {
    if (!changed) return;
    const next = (((at + by) % changed) + changed) % changed;
    setStep(next);
    const node = rows.current?.querySelector(`[data-change="${next}"]`);
    const viewport = rows.current?.closest<HTMLElement>(
      "[data-scroll-viewport]",
    );
    if (!node || !viewport) return;
    // Scroll this one container by hand. `scrollIntoView` walks every
    // scrollable ancestor, so stepping through groups also moved the page.
    viewport.scrollTop +=
      node.getBoundingClientRect().top -
      viewport.getBoundingClientRect().top -
      viewport.clientHeight / 2;
  };
  if (query.isPending) return <RulesAccessSkeleton />;
  if (query.isError)
    return (
      <p role="alert" className="p-4">
        Could not load sheet access. {editorError(query.error)}
      </p>
    );
  return (
    <section className="flex min-h-0 flex-col">
      <div
        className={cn(
          ARTIFACT_CONTROLS,
          "flex flex-wrap items-center gap-x-3 gap-y-1",
        )}
      >
        {/* The same stepper the written rules and the roster lead with, so
            every review pane is walked the same way. */}
        {!!changed && (
          <ChangeStepper
            noun="change"
            label={changed > 1 ? `Change ${at + 1} of ${changed}` : undefined}
            onStep={goTo}
          />
        )}
        <span className="text-subtle">
          {access?.keys.length ?? 0} sheet groups
        </span>
        <span className="text-subtle">
          {/* Before a comparison has run, what each key covers is simply not
              known: it is a property of the generated workbook. Saying "changes
              none of them" then would be a claim nobody has checked. */}
          {!access?.sheets_known
            ? "Run the comparison on the Pay tab to see what this changes"
            : changed
              ? `This request changes what ${changed === 1 ? "1 of them shows" : `${changed} of them show`}`
              : "This request changes none of them"}
        </span>
        <PaneSearchControl
          search={search}
          label="sheet groups"
          className="ml-auto"
        />
      </div>
      <div data-scroll-viewport className="min-h-0 flex-1 overflow-y-auto">
        <div ref={rows} className={PANE_BOTTOM_CLEARANCE}>
          {access && !access.keys.length && (
            <p className="px-3 py-2 text-subtle">
              Everyone with payroll access reads the whole workbook. No sheet
              groups are set up.
            </p>
          )}
          {access?.keys.map((entry) => (
            <AccessRow
              key={entry.key}
              entry={entry}
              change={steps[entry.key]}
              found={search.matches.includes(entry.key)}
              active={search.activeId === entry.key}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
